import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  TopToolbar,
  ExportButton,
  SelectInput,
  DateInput,
  ReferenceField,
  FunctionField,
  useRedirect,
} from 'react-admin';
import { Chip, Box } from '@mui/material';
import { Payment as PaymentIcon } from '@mui/icons-material';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import { PageTitle } from '../../components/PageTitle';

const PaymentFilters = [
  <DateInput key="dateFrom" source="dateFrom" label="From Date" />,
  <DateInput key="dateTo" source="dateTo" label="To Date" />,
];

const ListActions = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);

const PaymentMethodChip = ({ record }: { record: any }) => {
  const getMethodColor = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'cash':
        return 'success';
      case 'check':
        return 'info';
      case 'credit_card':
      case 'creditcard':
        return 'primary';
      case 'bank_transfer':
      case 'banktransfer':
        return 'secondary';
      case 'paypal':
        return 'warning';
      case 'other':
        return 'default';
      default:
        return 'default';
    }
  };

  const getMethodLabel = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'credit_card':
      case 'creditcard':
        return 'Credit Card';
      case 'bank_transfer':
      case 'banktransfer':
        return 'Bank Transfer';
      case 'cash':
        return 'Cash';
      case 'check':
        return 'Check';
      case 'paypal':
        return 'PayPal';
      case 'other':
        return 'Other';
      default:
        return method?.charAt(0).toUpperCase() + method?.slice(1) || 'Unknown';
    }
  };

  return (
    <Chip
      label={getMethodLabel(record.paymentMethod)}
      color={getMethodColor(record.paymentMethod) as any}
      size="small"
    />
  );
};

export const PaymentList = () => {
  const redirect = useRedirect();

  const handleCreatePayment = () => {
    redirect('/crm/payments/create');
  };

  return (
    <Box>
      <PageTitle
        title="Payments"
        titleKey="pages.payments"
        subtitle="Track received payments and transactions"
        icon={<PaymentIcon />}
      />
      <List
        actions={<ListActions />}
        perPage={25}
        sort={{ field: 'paymentDate', order: 'DESC' }}
        empty={<CustomEmptyState resource="payments" onAction={handleCreatePayment} />}
        sx={{ '& .RaList-main': { mt: 0 } }}
      >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <DateField source="paymentDate" label="Date" showTime />
        <ReferenceField source="invoiceId" reference="invoices" link="show" label="Invoice">
          <TextField source="invoiceNumber" />
        </ReferenceField>
        <ReferenceField source="customerId" reference="customers" link="show" label="Customer">
          <TextField source="displayName" />
        </ReferenceField>
        <NumberField
          source="amount"
          label="Amount"
          options={{ style: 'currency', currency: 'USD' }}
        />
        <FunctionField
          source="paymentMethod"
          label="Method"
          render={(record: any) => <PaymentMethodChip record={record} />}
        />
        <TextField source="notes" label="Notes" />
        <DateField source="createdAt" label="Recorded" showTime />
      </Datagrid>
    </List>
    </Box>
  );
};
